/**
 * Correspondencia entre las hojas del paquete y las rutas de la aplicación.
 *
 * Lo único que se declara aquí es lo que la interfaz sabe y la configuración
 * no: a qué ruta va cada hoja y con qué rótulo se lee en pantalla. El ORDEN y
 * el CONJUNTO de hojas salen de `config.closing.sheets`, que es
 * `config/mappings/package.yml` —la misma fuente que ordena el XLSX y el PDF—.
 *
 * Esa división no es purismo. El sidebar anterior tenía las hojas escritas a
 * mano y había derivado: ponía el Anexo ESF en cuarto lugar cuando el paquete
 * lo tiene en la 05, y Razones antes de Notas cuando el paquete las tiene
 * después. Un orden escrito dos veces se separa; uno derivado no puede.
 */

import type { ReactNode } from "react";

import type { ClosingSheet } from "../../api/types";
import type { Tab } from "../../components/Tabs";

interface Destino {
  /** Ruta relativa a `/closing`; vacía es la pestaña índice. */
  path: string;
  /** Rótulo de pantalla. El nombre de la hoja de Excel va como `title`. */
  label: string;
}

const DESTINOS: Record<string, Destino> = {
  closing_cover: { path: "", label: "Portada" },
  financial_dashboard: { path: "dashboard", label: "Dashboard Financiero" },
  esf_formal: { path: "esf", label: "Situación Financiera" },
  eri_formal: { path: "eri", label: "Resultado Integral" },
  esf_annex: { path: "annex", label: "Anexo ESF" },
  equity_statement: { path: "equity", label: "Cambios en el Patrimonio" },
  cash_flow_statement: { path: "cash-flow", label: "Flujos de Efectivo" },
  eri_monthly_detail: { path: "monthly", label: "Detalle ERI Mensual" },
  closing_notes: { path: "notes", label: "Notas" },
  financial_ratios: { path: "ratios", label: "Razones Financieras" },
  accounting_policy: { path: "policy", label: "Técnica Contable" },
};

/**
 * Las sub-vistas de un estado: el detalle por cuenta y la conciliación mensual.
 *
 * Son reportes del catálogo mostrados DENTRO de la hoja que explican, así que su
 * descarga tiene que salir del mismo menú que la de la hoja. Si tuvieran botones
 * propios, en `/closing/cash-flow/detail` volvería a haber dos controles de
 * exportación, que es justo lo que se acaba de unificar.
 */
const SUBVISTAS: Record<string, { reportKey: string; label: string }> = {
  "cash-flow/detail": { reportKey: "cash_flow", label: "Detalle por cuenta" },
  "cash-flow/reconciliation": {
    reportKey: "cash_flow_summary",
    label: "Conciliación mensual",
  },
  "equity/detail": { reportKey: "equity_changes", label: "Detalle por cuenta" },
};

/** El reporte de la sub-vista abierta, si la ruta es una de ellas. */
export function subViewForPath(pathname: string): { reportKey: string; label: string } | undefined {
  const resto = pathname.replace(/^\/closing\/?/, "").replace(/\/$/, "");
  return SUBVISTAS[resto];
}

/**
 * La hoja que corresponde a una ruta, para ofrecer su descarga individual.
 *
 * Sale del MISMO mapa que las pestañas: si mañana una hoja cambia de ruta, la
 * pestaña y su botón de descarga se mueven juntos o no se mueve ninguno.
 */
export function sheetForPath(sheets: ClosingSheet[], pathname: string): ClosingSheet | undefined {
  const resto = pathname.replace(/^\/closing\/?/, "").replace(/\/$/, "");
  // Las sub-pestañas —`cash-flow/detail`— pertenecen a su hoja: se descarga el
  // estado, no la vista de detalle que se esté mirando.
  const raiz = resto.split("/")[0] ?? "";
  const clave = Object.keys(DESTINOS).find((key) => DESTINOS[key].path === raiz);
  return clave ? sheets.find((sheet) => sheet.key === clave) : undefined;
}

/**
 * Las pestañas del paquete, en el orden del Excel.
 *
 * `support_only` se respeta tal cual: el Balance de Comprobación está marcado
 * así en `package.yml` porque no es hoja del paquete firmable sino soporte, y
 * eso ya lo dice el dato. Filtrarlo aquí no es una decisión de interfaz.
 *
 * Una hoja sin destino se omite en silencio a propósito: si mañana se añade una
 * al paquete, la barra sigue funcionando en vez de romper la sección entera. Que
 * no falte ninguna lo defiende una prueba de Python sobre `package.yml`.
 */
export function closingTabs(
  sheets: ClosingSheet[],
  badge?: (sheet: ClosingSheet) => ReactNode,
): Tab[] {
  return sheets
    .filter((sheet) => !sheet.support_only && sheet.key in DESTINOS)
    .map((sheet) => {
      const destino = DESTINOS[sheet.key];
      return {
        to: destino.path ? `/closing/${destino.path}` : "/closing",
        label: destino.label,
        seq: sheet.seq,
        title: `Hoja «${sheet.sheet}» del paquete`,
        end: destino.path === "",
        badge: badge?.(sheet),
      };
    });
}
